// hooks/use-admins.ts
import { FormEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from '../../../Redux/store';
import { ADMIN_TYPES } from '../../../utils/Enums';
import { useGetAdmins } from '../../../apis/admins/get-admins';
import { useDeleteAdmin } from '../../../apis/admins/delete-admin';

const useAdmins = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [selectedAdminId, setSelectedAdminId] = useState("");
    const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);

    const user = useAppSelector((state) => state.auth.user);
    const isAdmin = user?.userType === ADMIN_TYPES.ADMIN;

    const { data, isFetching, refetch } = useGetAdmins({
        page: searchParams.get("page") || 1,
        limit: 10,
        search: searchParams.get("search") || "",
    });

    const { mutateAsync: deleteAdmin, isPending: isDeletingAdmin } = useDeleteAdmin();

    const admins = data?.docs ?? [];
    const totalPages = data?.totalPages ?? 0;

    const updateUrlParams = (newParams: Record<string, string>) => {
        const updatedParams = new URLSearchParams(searchParams);
        Object.entries(newParams).forEach(([key, value]) => {
            value ? updatedParams.set(key, value) : updatedParams.delete(key);
        });
        setSearchParams(updatedParams);
    };

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        updateUrlParams({ search, page: "1" });
    };

    const handleDeleteAdmin = async () => {
        try {
            const res = await deleteAdmin(selectedAdminId);
            toast.success(res?.message);
            if (admins.length <= 1 && parseInt(searchParams.get("page") || "1") > 1) {
                updateUrlParams({ page: (parseInt(searchParams.get("page") || "1") - 1).toString() });
            } else {
                refetch();
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.error || "Something went wrong");
        }
        setSelectedAdminId("");
    };

    return {
        admins,
        totalPages,
        isFetching,
        isDeletingAdmin,
        search,
        setSearch,
        selectedAdminId,
        setSelectedAdminId,
        isAddAdminOpen,
        setIsAddAdminOpen,
        user,
        isAdmin,
        searchParams,
        updateUrlParams,
        handleSearch,
        handleDeleteAdmin,
        refetch,
    };
};

export default useAdmins;